import { env } from '../../config/env'
import { useAuthStore } from '../auth/store'

// Базовые endpoints
const endpoints = {
  orders: `${env.resolveApiUrl()}/orders`
}

// Функция для получения токена авторизации (как в http.ts)
function getAuthToken(): string | null {
  try {
    const storeToken = useAuthStore.getState().token
    const lsToken = localStorage.getItem('alfastil_token')
    const webToken = localStorage.getItem('alfastil_web_token')
    
    // 🔥 ИСПРАВЛЕНИЕ: Проверяем токены на истечение
    const isTokenValid = (tokenToCheck: string | null): boolean => {
      if (!tokenToCheck) return false
      try {
        const payload = JSON.parse(atob(tokenToCheck.split('.')[1]))
        const now = Math.floor(Date.now() / 1000)
        return payload.exp && payload.exp >= now
      } catch {
        return false
      }
    }
    
    // Возвращаем первый валидный токен
    if (isTokenValid(storeToken)) return storeToken
    if (isTokenValid(lsToken)) return lsToken
    if (isTokenValid(webToken)) return webToken
    return null
  } catch {
    // Fallback если store недоступен
    return localStorage.getItem('alfastil_token')
  }
}

export interface PriceSubmissionData {
  price: number
  delivery_time: number
  comment?: string
}

export interface RejectOrderData {
  reason: string
}

/**
 * API для действий с заказами
 */
export class OrderActionsApi {
  
  /**
   * Отметить заказ как просмотренный (переход в статус "watching")
   */
  static async markOrderAsViewed(orderId: string): Promise<void> {
    const response = await fetch(`${endpoints.orders}/${orderId}/mark-viewed`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${getAuthToken()}`
      }
    })

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Ошибка сети' }))
      throw new Error(error.message || 'Не удалось отметить заказ как просмотренный')
    }

    const data = await response.json()
    if (!data.success) {
      throw new Error('Не удалось отметить заказ как просмотренный')
    }
  }

  /**
   * Взять заказ на просчет (переход в статус "calculating")
   */
  static async takeOrderForCalculation(orderId: string): Promise<void> {
    const response = await fetch(`${endpoints.orders}/${orderId}/take`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${getAuthToken()}`
      }
    })

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Ошибка сети' }))
      throw new Error(error.message || 'Не удалось взять заказ на просчет')
    }

    const data = await response.json()
    if (!data.success) {
      throw new Error('Не удалось взять заказ на просчет')
    }
  }

  /**
   * Предложить цену (переход в статус "price_given")
   */
  static async submitPrice(orderId: string, priceData: PriceSubmissionData): Promise<void> {
    const response = await fetch(`${endpoints.orders}/${orderId}/price`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${getAuthToken()}`
      },
      body: JSON.stringify(priceData)
    })

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Ошибка сети' }))
      throw new Error(error.message || 'Не удалось отправить цену')
    }

    const data = await response.json()
    if (!data.success) {
      throw new Error('Не удалось отправить цену')
    }
  }

  /**
   * Отказаться от заказа (переход в статус "rejected")
   */
  static async rejectOrder(orderId: string, rejectData: RejectOrderData): Promise<void> {
    const response = await fetch(`${endpoints.orders}/${orderId}/reject`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${getAuthToken()}`
      },
      body: JSON.stringify(rejectData)
    })

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Ошибка сети' }))
      throw new Error(error.message || 'Не удалось отказаться от заказа')
    }

    const data = await response.json()
    if (!data.success) {
      throw new Error('Не удалось отказаться от заказа')
    }
  }



  /**
   * Запустить заказ в работу (переход в статус "executing")
   */
  static async executeOrder(orderId: string): Promise<void> {
    const response = await fetch(`${endpoints.orders}/${orderId}/execute`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${getAuthToken()}`
      }
    })

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Ошибка сети' }))
      throw new Error(error.message || 'Не удалось запустить заказ в работу')
    }

    const data = await response.json()
    if (!data.success) {
      throw new Error('Не удалось запустить заказ в работу')
    }
  }

  /**
   * Подтвердить заказ (переход в статус "confirmed")
   */
  static async confirmOrder(orderId: string): Promise<void> {
    const response = await fetch(`${endpoints.orders}/${orderId}/confirm`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${getAuthToken()}`
      }
    })

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Ошибка сети' }))
      throw new Error(error.message || 'Не удалось подтвердить заказ')
    }

    const data = await response.json()
    if (!data.success) {
      throw new Error('Не удалось подтвердить заказ')
    }
  }

  /**
   * Отклонить заказ клиентом (переход в статус "declined")
   */
  static async declineOrder(orderId: string, rejectData: RejectOrderData): Promise<void> {
    const response = await fetch(`${endpoints.orders}/${orderId}/decline`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${getAuthToken()}`
      },
      body: JSON.stringify(rejectData)
    })

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Ошибка сети' }))
      throw new Error(error.message || 'Не удалось отклонить заказ')
    }

    const data = await response.json()
    if (!data.success) {
      throw new Error('Не удалось отклонить заказ')
    }
  }

  /**
   * Восстановить заказ
   */
  static async restoreOrder(orderId: string, reason: string): Promise<void> {
    const response = await fetch(`${endpoints.orders}/${orderId}/restore`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${getAuthToken()}`
      },
      body: JSON.stringify({ reason })
    })

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Ошибка сети' }))
      throw new Error(error.message || 'Не удалось восстановить заказ')
    }

    const data = await response.json()
    if (!data.success) {
      throw new Error('Не удалось восстановить заказ')
    }
  }
}

export default OrderActionsApi
